import { Component, OnDestroy, HostBinding } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { delay, filter, map, distinctUntilChanged } from 'rxjs/operators';
import {
  noOpAnimation,
  DealVersion,
  Order,
  Session,
  getSession,
  DealFacade,
  selectOrders
} from '@ipreo/muni-shared';
import { GameDayRootReducerState } from '../../../state/root.state';
import { SetRawData } from '../../../state/data/actions';
import { GameDayModel } from '../../../models/gameday-model';
import {
  BillingService,
  DEAL_PUSH_TIMEOUT
} from '../../services/billing.service';
import {
  getGamedaySettingsState,
  getGamedayDealState
} from '../../../state/data/reducer';
import { GamedaySettingsState } from '../../../state/data/state';
import { actualIssuePricingDate } from '../../mappers/actual-issue-pricing-date.mapper';

@Component({
  selector: 'muni-gameday-gameday',
  styleUrls: ['./gameDay.styles.scss'],
  templateUrl: './gameDay.template.html',
  animations: noOpAnimation,
  standalone: false,
})
export class GameDayComponent implements OnDestroy {
  @HostBinding('@noOpAnimation')
  public readonly noOpAnimation: boolean = true;

  @HostBinding('style.display')
  public readonly display: string = 'block';

  @HostBinding('style.height')
  public readonly height: string = 'calc(100% - 38px)';

  @HostBinding('style.width')
  public readonly width: string = '100%';

  private readonly deal: Observable<DealVersion> = this.dealFacade.dealState$.pipe(
    filter((dealInfo) => dealInfo.loaded),
    map((dealInfo) => dealInfo.deal)
  );

  private readonly orders: Observable<Order[]> = this.store.pipe(
    select(selectOrders),
    filter((orders) => !!orders)
  );

  public readonly isCopyEnabled: Observable<boolean> = this.store.pipe(
    select(getGamedayDealState),
    map(
      (deal: GameDayModel.Deal) => !!deal && deal.gamedaySettings.allowExport
    ),
    distinctUntilChanged()
  );

  public readonly dealDate: Observable<Date> = this.store.pipe(
    select(getGamedayDealState),
    map(actualIssuePricingDate),
    distinctUntilChanged()
  );

  public readonly marketDrawerHidden: Observable<boolean> = this.store.pipe(
    select(getGamedaySettingsState),
    map(({ showMarketDrawers }: GamedaySettingsState) => !showMarketDrawers)
  );

  public readonly marketDrawerEnabled: Observable<boolean> = this.store.pipe(
    select(getSession),
    map(
      (session) => !!session && session.deptType !== Session.DeptType.SyndDesk
    )
  );

  private readonly dealWithOrders: Observable<GameDayModel.RawData> = combineLatest(
    this.deal,
    this.orders
  ).pipe(
    map(([deal, orders]) => GameDayModel.fromStoreModels(deal, orders)),
    distinctUntilChanged(GameDayModel.isRawDataEqual)
  );

  private readonly subscription: Subscription = this.dealWithOrders.subscribe(
    (dealWithOrders: GameDayModel.RawData) =>
      this.store.dispatch(new SetRawData(dealWithOrders))
  );

  private readonly billingSubscription: Subscription = this.deal
    .pipe(
      delay(DEAL_PUSH_TIMEOUT),
      map((deal) => deal.id),
      distinctUntilChanged()
    )
    .subscribe((id) => {
      this.billingService.report(id);
    });

  constructor(
    private store: Store<GameDayRootReducerState>,
    private dealFacade: DealFacade,
    private billingService: BillingService
  ) {}

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.billingSubscription.unsubscribe();
  }
}
